import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Calendar, FileText, X, Save, Upload, DollarSign, Tag, User, Loader2 } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { useServices, useDeleteConfirmation, useSection } from '@/app/providers';
import { Category, Expense, ExpenseCategory, SectionStatus } from '@/shared/types';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { useCrudNotifications, usePaginatedList, useFileUpload, useDateFormatter } from '@/shared/hooks';
import { DataTable, SearchableSelect, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';

export const AdminExpenseList = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const { formatDate } = useDateFormatter();
  const { confirmDelete } = useDeleteConfirmation();
  const { expenseRepo, categoryRepo } = useServices();
  const { withCreateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Egreso',
  });
  const canManageExpense = ability.can('manage', 'all') || ability.can('manage', 'Expense');
  const canCreateExpense = canManageExpense || ability.can('create', 'Expense');
  const canDeleteExpense = canManageExpense || ability.can('delete', 'Expense');

  // Sección del gasto (§25). El admin de sección solo registra en la suya (el
  // backend la asigna si no se manda); el admin puede dejarlo como general.
  const { sections, hasSections, isSectionAdmin, selectedSectionId } = useSection();
  const sectionOptions = useMemo(
    () =>
      sections
        .filter((section) => section.status === SectionStatus.ACTIVE)
        .map((section) => ({ value: section.id, label: section.name })),
    [sections],
  );

  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    categoryRepo.getActive('EXPENSE').then(setCategories).catch(() => setCategories([]));
  }, [categoryRepo]);

  const {
    data: expenses,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = usePaginatedList({
    // `categoryId: ''` no viaja (buildQueryString descarta vacíos). Como filtro
    // del hook, cambiarla vuelve a la página 1 y conserva la búsqueda.
    fetchFn: (params) => expenseRepo.getExpenses(params),
    initialFilters: { categoryId: '' },
    errorTitle: 'Error al cargar egresos',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  // El admin de sección registra el gasto en su sección activa por defecto.
  const emptyExpense = (): Partial<Expense> => ({
    category: ExpenseCategory.MAINTENANCE,
    date: new Date().toISOString().split('T')[0],
    status: 'PAID',
    sectionId: isSectionAdmin ? (selectedSectionId ?? undefined) : undefined,
  });
  const [newExpense, setNewExpense] = useState<Partial<Expense>>(emptyExpense);

  // File upload hook
  const { upload, isUploading, reset: resetUpload } = useFileUpload('documents');

  const handleDelete = async (id: string, title: string) => {
    if (!canDeleteExpense) return;
    await confirmDelete({
      itemName: title,
      itemType: 'Egreso',
      onConfirm: async () => {
        await withDeleteNotification(() => expenseRepo.deleteExpense(id));
        refresh();
      }
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateExpense) return;
    if (!newExpense.title || !newExpense.amount) return;

    try {
      await withCreateNotification(() => expenseRepo.createExpense(newExpense as Expense));
      setIsModalOpen(false);
      setNewExpense(emptyExpense());
      resetUpload();
      refresh();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await upload(file);
      if (result) {
        setNewExpense({
          ...newExpense,
          invoiceUrl: result.url,
          invoiceFileId: result.id,
        });
      }
    }
  };

  const getCategoryLabel = (cat: ExpenseCategory) => {
    return t(`admin.expenses.cat.${cat.toLowerCase()}`);
  };

  const columns: ColumnDef<Expense>[] = useMemo(() => [
    {
      id: 'concept',
      header: t('admin.expenses.table.concept'),
      cell: (expense) => (
        <div>
          <p className="font-bold text-slate-800">{expense.title}</p>
          <p className="text-xs text-slate-500">{expense.provider || '-'}</p>
          {expense.invoiceUrl && (
            <a href={expense.invoiceUrl} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 mt-1">
              <FileText size={10} /> Ver Factura
            </a>
          )}
        </div>
      ),
    },
    {
      id: 'category',
      header: t('admin.expenses.table.category'),
      cell: (expense) => (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600"
        >
          {expense.categoryColor && (
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: expense.categoryColor }} />
          )}
          {expense.categoryName ?? getCategoryLabel(expense.category)}
        </span>
      ),
    },
    {
      id: 'amount',
      header: t('admin.expenses.table.amount'),
      className: 'text-right',
      cell: (expense) => (
        <span className="font-mono text-slate-800 font-medium">
          -${expense.amount.toLocaleString()}
        </span>
      ),
    },
    {
      id: 'date',
      header: t('admin.expenses.table.date'),
      cell: (expense) => (
        <div className="flex items-center gap-2 text-slate-600">
          <Calendar size={14} className="text-slate-400" />
          {formatDate(expense.date)}
        </div>
      ),
    },
  ], [t, formatDate]);

  return (
    <>
      <DataTable
        data={expenses}
        columns={columns}
        keyExtractor={(expense) => expense.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title={t('admin.expenses.title')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar egreso..."
        loading={loading}
        emptyMessage="No hay egresos registrados."
        actions={
          (categories.length > 0 || canCreateExpense) ? (
            <div className="flex items-center gap-2">
              {categories.length > 0 && (
                <select
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-600"
                  value={filters.categoryId}
                  onChange={(e) => setFilters({ categoryId: e.target.value })}
                >
                  <option value="">Todas las categorías</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              )}
              {canCreateExpense && (
                <Button onClick={() => setIsModalOpen(true)} icon={<Plus size={16}/> }>
                  {t('admin.expenses.create')}
                </Button>
              )}
            </div>
          ) : null
        }
        rowActions={(expense: { id: string; title: string; }) => canDeleteExpense ? (
          <button
            onClick={() => handleDelete(expense.id, expense.title)}
            className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors"
          >
            <Trash2 size={16} />
          </button>
        ) : null}
      />

      {isModalOpen && canCreateExpense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{t('admin.expenses.modal.title')}</h3>
              <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <Input
                label={t('admin.expenses.modal.desc')}
                value={newExpense.title || ''}
                onChange={e => setNewExpense({...newExpense, title: e.target.value})}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t('admin.expenses.modal.amount')}
                  type="number"
                  icon={<DollarSign size={14}/>}
                  value={newExpense.amount || ''}
                  onChange={e => setNewExpense({...newExpense, amount: parseFloat(e.target.value)})}
                  required
                />
                 <Input
                  label={t('admin.expenses.modal.date')}
                  type="date"
                  value={newExpense.date || ''}
                  onChange={e => setNewExpense({...newExpense, date: e.target.value})}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.expenses.modal.category')}</label>
                <div className="relative">
                  <Tag className="absolute left-3 top-2.5 text-slate-400 pointer-events-none" size={16}/>
                  {categories.length > 0 ? (
                    <select
                      className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm"
                      value={newExpense.categoryId ?? ''}
                      onChange={e => setNewExpense({...newExpense, categoryId: e.target.value || undefined})}
                    >
                      <option value="" disabled>Selecciona una categoría</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  ) : (
                    <select
                      className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm"
                      value={newExpense.category}
                      onChange={e => setNewExpense({...newExpense, category: e.target.value as ExpenseCategory})}
                    >
                      <option value={ExpenseCategory.MAINTENANCE}>{t('admin.expenses.cat.maintenance')}</option>
                      <option value={ExpenseCategory.UTILITIES}>{t('admin.expenses.cat.utilities')}</option>
                      <option value={ExpenseCategory.SALARIES}>{t('admin.expenses.cat.salaries')}</option>
                      <option value={ExpenseCategory.CLEANING}>{t('admin.expenses.cat.cleaning')}</option>
                      <option value={ExpenseCategory.SECURITY}>{t('admin.expenses.cat.security')}</option>
                      <option value={ExpenseCategory.INSURANCE}>{t('admin.expenses.cat.insurance')}</option>
                      <option value={ExpenseCategory.SERVICES}>{t('admin.expenses.cat.services')}</option>
                      <option value={ExpenseCategory.PAYROLL}>{t('admin.expenses.cat.payroll')}</option>
                      <option value={ExpenseCategory.SUPPLIES}>{t('admin.expenses.cat.supplies')}</option>
                      <option value={ExpenseCategory.REFUND}>{t('admin.expenses.cat.refund')}</option>
                      <option value={ExpenseCategory.OTHER}>{t('admin.expenses.cat.other')}</option>
                    </select>
                  )}
                </div>
              </div>

              <Input
                label={t('admin.expenses.modal.provider')}
                icon={<User size={14}/>}
                value={newExpense.provider || ''}
                onChange={e => setNewExpense({...newExpense, provider: e.target.value})}
              />

              {hasSections && (
                <SearchableSelect
                  label="Sección"
                  options={sectionOptions}
                  value={newExpense.sectionId ?? null}
                  onChange={(sectionId) => setNewExpense({ ...newExpense, sectionId })}
                  placeholder={
                    !isSectionAdmin
                      ? 'General (todo el residencial)'
                      : sectionOptions.length === 1
                        ? sectionOptions[0].label
                        : 'Selecciona una sección'
                  }
                  isSearchable={false}
                  isDisabled={isSectionAdmin && sectionOptions.length <= 1}
                  isClearable={!isSectionAdmin}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('admin.expenses.modal.receipt')}</label>
                <div className="flex items-center gap-3">
                   <label className={`cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors w-full ${isUploading ? 'opacity-50 cursor-wait' : ''}`}>
                      {isUploading ? (
                        <Loader2 size={16} className="mr-2 text-slate-500 animate-spin" />
                      ) : (
                        <Upload size={16} className="mr-2 text-slate-500" />
                      )}
                      {isUploading ? 'Subiendo...' : newExpense.invoiceUrl ? 'Archivo Adjunto (Cambiar)' : 'Subir Archivo'}
                      <input type="file" className="hidden" disabled={isUploading} onChange={handleFileUpload} />
                   </label>
                   {newExpense.invoiceUrl && <span className="text-xs text-emerald-600 font-bold">OK</span>}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
